import React, { FC, useContext, useMemo } from 'react';

// components
import AampeToggleButton, {
    ToggleButtonProps,
} from '../../../components/shared/AampeToggleButton/AampeToggleButton';
import styled from 'styled-components';

// constants
import aampeTheme from '../../../constants/aampeTheme';
import { FormulaType } from '../../../constants/formulaType';

// contexts
import { EditorContext } from '../../../context/EditorContext';

// models
import { FormulaTypesData, PreviewView } from '../../../models/formula.model';

import PhoneView from './PhoneView';

//helpers
interface StyledProps {
    isSecondaryView?: boolean;
    showTestPreview?: boolean;
}

interface MessagePreviewMainProps {
    isCoverPreview?: boolean;
    className?: string;
    showTestPreview?: boolean;
}

const MessagePreviewMain: FC<React.PropsWithChildren<MessagePreviewMainProps>> =
    ({ isCoverPreview, className, showTestPreview }) => {
        const { editorDetails, setEditorDetails } = useContext(EditorContext);

        const { formulaType, isSecondaryView } = editorDetails;
        const toggleButtons = useMemo<ToggleButtonProps[]>(() => {
            return formulaType === FormulaType.email
                ? ([
                      {
                          title: 'Desktop',
                          isSelected: isSecondaryView
                              ? PreviewView.MOBILE
                              : PreviewView.DESKTOP,
                          onChange: () =>
                              setEditorDetails({
                                  ...editorDetails,
                                  isSecondaryView: false,
                              }),
                          value: PreviewView.DESKTOP,
                          isLeft: true,
                          something: false,
                      },
                      {
                          title: 'Mobile',
                          isSelected: isSecondaryView
                              ? PreviewView.DESKTOP
                              : PreviewView.MOBILE,
                          onChange: () =>
                              setEditorDetails({
                                  ...editorDetails,
                                  isSecondaryView: true,
                              }),
                          value: PreviewView.MOBILE,
                          isRight: true,
                      },
                  ] as ToggleButtonProps[])
                : ([
                      {
                          title: 'iOS',
                          isSelected: isSecondaryView
                              ? PreviewView.ANDROID
                              : PreviewView.IOS,
                          onChange: () =>
                              setEditorDetails({
                                  ...editorDetails,
                                  isSecondaryView: false,
                              }),
                          value: PreviewView.IOS,
                          isLeft: true,
                      },
                      {
                          title: 'Android',
                          isSelected: isSecondaryView
                              ? PreviewView.ANDROID
                              : PreviewView.IOS,
                          onChange: () =>
                              setEditorDetails({
                                  ...editorDetails,
                                  isSecondaryView: true,
                              }),
                          value: PreviewView.ANDROID,
                          isRight: true,
                      },
                  ] as ToggleButtonProps[]);
        }, [formulaType, isSecondaryView, setEditorDetails, editorDetails]);

        return (
            <Container className={className}>
                {FormulaTypesData[formulaType as FormulaType]
                    ?.supportMultipleViews &&
                    formulaType === FormulaType.email && (
                        <StyledToggleButtons
                            isSecondaryView={isSecondaryView}
                            buttons={toggleButtons}
                        />
                    )}
                <PhoneView
                    isCoverPreview={isCoverPreview}
                    showTestPreview={showTestPreview}
                />
                {FormulaTypesData[formulaType as FormulaType]
                    ?.supportMultipleViews &&
                    formulaType !== FormulaType.email && (
                        <PlatformToggleButtons
                            isSecondaryView={isSecondaryView}
                            buttons={toggleButtons}
                        />
                    )}
            </Container>
        );
    };

export default MessagePreviewMain;

const Container = styled.div((showTestPreview) => ({
    display: 'flex',
    margin: !showTestPreview ? 'auto' : undefined,
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
}));

const StyledToggleButtons = styled(AampeToggleButton).withConfig({
    shouldForwardProp: (prop) => prop !== 'isSecondaryView',
})<StyledProps>(({ isSecondaryView }) => ({
    backgroundColor: aampeTheme.colors.gray50,
    marginTop: aampeTheme.spacing4(5),
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    '& > :first-child': {
        border: 'none',
        borderRight: isSecondaryView
            ? 'none'
            : `1px solid ${aampeTheme.colors.bordersWG01}`,
        backgroundColor: isSecondaryView
            ? aampeTheme.colors.gray50
            : aampeTheme.colors.white,
        borderRadius: aampeTheme.borderRadius.default,
        '& > div': {
            padding: aampeTheme.spacing(0.5, 2),
        },
    },
    '& > :last-child': {
        border: 'none',
        borderLeft: isSecondaryView
            ? `1px solid ${aampeTheme.colors.bordersWG01}`
            : 'none',
        backgroundColor: isSecondaryView
            ? aampeTheme.colors.white
            : aampeTheme.colors.gray50,
        borderRadius: aampeTheme.borderRadius.default,
        '& > div': {
            padding: aampeTheme.spacing(0.5, 2),
        },
    },
}));

const PlatformToggleButtons = styled(AampeToggleButton).withConfig({
    shouldForwardProp: (prop) => prop !== 'isSecondaryView',
})<StyledProps>(({ isSecondaryView }) => ({
    backgroundColor: aampeTheme.colors.backgroundWG02,
    marginTop: aampeTheme.spacing4(5),
    borderRadius: aampeTheme.borderRadius.default,
    padding: '2px',
    gap: '4px',
    display: 'inline-flex',
    '& > :first-child': {
        border: 'none',
        backgroundColor: !isSecondaryView
            ? aampeTheme.colors.white
            : 'transparent',
        color: !isSecondaryView
            ? aampeTheme.colors.solidA01
            : aampeTheme.colors.solidWG02,
        borderRadius: aampeTheme.borderRadius.default,
        '& > div': {
            fontWeight: aampeTheme.weights.medium,
        },
    },
    '& > :last-child': {
        border: 'none',
        backgroundColor: isSecondaryView
            ? aampeTheme.colors.white
            : 'transparent',
        color: isSecondaryView
            ? aampeTheme.colors.solidA01
            : aampeTheme.colors.solidWG02,
        borderRadius: aampeTheme.borderRadius.default,
        '& > div': {
            fontWeight: aampeTheme.weights.medium,
        },
    },
}));
